import React, { useEffect, useRef } from 'react'
import Button from 'components/Button'
import useStore from 'hooks/useStore'
import Post from 'components/Post'
import './index.scss'

interface TabsManageProps {
  notifications: notifications.Notification[]
  notificationHasMore: boolean
  post: book.BookEntry[]
  postHasMore: boolean
  body: board.BoardResponse | null | undefined
  activeTab: string
  handleUpload: (id: string) => void
  setActiveTab: (tab: string) => void
  setBookId: (tab: string) => void
  setInitialSlide: (idx: number) => void
  loadMorePosts: () => void
  loadMoreNotifications: () => void
  setModal: (modal: string) => void
  setNotificationId: (id: string) => void
  setNotificationRelated: (body: notifications.Related | null) => void
}

const TabsManage: React.FC<TabsManageProps> = ({
  notifications,
  notificationHasMore,
  post,
  postHasMore,
  body,
  activeTab,
  setBookId,
  setActiveTab,
  setInitialSlide,
  loadMoreNotifications,
  loadMorePosts,
  setModal,
  setNotificationId,
  handleUpload,
  setNotificationRelated }) => {
  const tabs = ['notifications', 'info', 'photos', 'book']
  const startX = useRef(0)
  const startY = useRef(0)
  const isHorizontalSwipe = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const openStore = useStore()
  const handleTabClick = (tab: string) => {
    setActiveTab(tab)
  }
  const handleTouchStart = (e: TouchEvent) => {
    startX.current = e.touches[0].clientX
    startY.current = e.touches[0].clientY
    isHorizontalSwipe.current = false
  }

  const handleTouchMove = (e: TouchEvent) => {
    const deltaX = e.touches[0].clientX - startX.current
    const deltaY = e.touches[0].clientY - startY.current

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      isHorizontalSwipe.current = true
      e.preventDefault()
    }
  }

  const handleTouchEnd = (e: TouchEvent) => {
    if (!isHorizontalSwipe.current) {
      return
    }

    const endX = e.changedTouches[0].clientX
    const deltaX = endX - startX.current

    if (deltaX < -50) {
      const currentIndex = tabs.indexOf(activeTab)
      if (currentIndex < tabs.length - 1) {
        handleTabClick(tabs[currentIndex + 1])
      }
    } else if (deltaX > 50) {
      const currentIndex = tabs.indexOf(activeTab)
      if (currentIndex > 0) {
        handleTabClick(tabs[currentIndex - 1])
      }
    }
  }

  useEffect(() => {
    const manageHead = document.querySelector('.manage__head') as HTMLElement
    const profileTabsHead = document.querySelector('.profile__tabs__head') as HTMLElement
    const profileTabsBody = document.querySelector('.profile__tabs__body') as HTMLElement

    if (manageHead && profileTabsHead && profileTabsBody) {
      const manageHeadHeight = manageHead.clientHeight
      const profileTabsHeadHeight = profileTabsHead.clientHeight
      const totalHeight = manageHeadHeight + profileTabsHeadHeight + 60

      profileTabsBody.style.height = `calc(calc(100 * var(--vh, 1vh)) - ${totalHeight}px - 2px)`
    }
  }, [])


  useEffect(() => {
    const container = containerRef.current
    container?.addEventListener('touchstart', handleTouchStart)
    container?.addEventListener('touchmove', handleTouchMove, { passive: false })
    container?.addEventListener('touchend', handleTouchEnd)

    return () => {
      container?.removeEventListener('touchstart', handleTouchStart)
      container?.removeEventListener('touchmove', handleTouchMove)
      container?.removeEventListener('touchend', handleTouchEnd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  return (
    <>
      <div className="profile__tabs">
        <div className="profile__tabs__head">
          <div className="wrap">
            {tabs.map(tab => (
              <span
                key={tab}
                className={activeTab === tab ? 'active' : ''}
                onClick={() => handleTabClick(tab)}
              >
                {tab === 'notifications' && 'Powiadomienia'}
                {tab === 'info' && 'Dane'}
                {tab === 'photos' && 'Materiały'}
                {tab === 'book' && 'Księga'}
              </span>
            ))}
          </div>
        </div>
        <div className="profile__tabs__body" ref={containerRef}>
          {tabs.map((tab, index) => {
            const activeIndex = tabs.indexOf(activeTab)
            let slideClass = ''
            if (index < activeIndex) {
              slideClass = 'slide-in-left'
            } else if (index > activeIndex) {
              slideClass = 'slide-in-right'
            }
            return (
              <div
                key={tab}
                className={`tab-content ${tab} ${activeTab === tab ? 'active' : ''} ${slideClass}`}>
                {tab === 'notifications' && (
                  <div className="notificationsWrap">
                    {!body?.data.is_paid &&
                      <div
                        className="notification warning"
                        onClick={() => {
                          if (!body?.data?.to_order) return
                          const { product_key, params } = body?.data.to_order
                          openStore(product_key, { related: params.related, profileName: body.data.name })
                        }}>
                        <h2>Opłać profil</h2>
                        <span>Aby opublikować profil musisz go opłacić</span>
                        <div className="action">
                          <div className="wrap"><i className="icon icon--arrow-right"></i></div>
                        </div>
                      </div>}
                    {body?.data.is_paid && !body?.data.is_published &&
                      <div className="notification warning" onClick={() => setModal('publish')}>
                        <h2>Opublikuj wirtualny profil</h2>
                        <span>Aby profil był widoczny dla innych, kliknij i opublikuj go</span>
                        <div className="action">
                          <div className="wrap"><i className="icon icon--arrow-right"></i></div>
                        </div>
                      </div>}
                    {notifications.map(notification => (
                      <div
                        key={notification.id}
                        className="notification"
                        onClick={notification.needAction || notification.type === 'new_candle'
                          ? () => {
                            setNotificationRelated(notification.related)
                            setNotificationId(notification.id)
                            setModal('notificationModal')
                          }
                          : undefined}>
                        <h2>{notification.name}</h2>
                        <span>{notification.description}</span>
                        <div className="action">
                          <div className="wrap"><i className="icon icon--arrow-right"></i></div>
                        </div>
                      </div>
                    ))}
                    {notificationHasMore && <button className="btn btn--empty" onClick={loadMoreNotifications}>Załaduj więcej</button>}
                    {
                      body?.data.is_paid && body?.data.is_published && notifications.length === 0 &&
                      <div className='empty'>Brak powiadomień</div>
                    }
                  </div>
                )
                }
                {tab === 'info' && (
                  <div className='infoWrap'>
                    <div className="myInput">
                      <label>Imię i nazwisko</label>
                      <div className='empty'>{body?.data.name}</div>
                    </div>

                    <div className="myInput">
                      <label>Sentencja</label>
                      <div className='empty'>{body?.data.sentence}</div>
                    </div>

                    <div className="myInput">
                      <label>Data urodzenia</label>
                      <div className='empty'>{body?.data.birth}</div>
                    </div>

                    <div className="myInput">
                      <label>Data śmierci</label>
                      <div className='empty'>{body?.data.death}</div>
                    </div>

                    <div className="myInput">
                      <label>Lokalizacja pomnika</label>
                      <div className='empty'>{body?.data.gps ? body?.data.gps.latitude + ', ' + body?.data.gps.longitude : 'Brak lokalizacji'}</div>
                    </div>

                    <div className="myInput">
                      <label>Biografia</label>
                      <div
                        className='biographyHtml empty large'
                        dangerouslySetInnerHTML={body?.data.biography ? { __html: body.data.biography } : undefined} />
                    </div>

                    <Button type='empty' text='Edytuj dane' action={() => setModal('editData')} />
                  </div>)}
                {tab === 'photos' && (
                  <>
                    <button className='btn btn--add' onClick={() => setModal('addphotos')}>Dodaj zdjęcia lub filmy</button>
                    <div className="notificationsWrap">
                      <div
                        className={`notification ${body?.data.quota?.images.exceeded ? 'warning' : ''}`}>
                        <h2>Limit zdjęć{body?.data.quota?.images.exceeded ? ' - wykorzystany!' : ''}</h2>
                        <span>Wykorzystano: {body?.data.quota?.images.current}/{body?.data.quota?.images.max} {body?.data.quota?.images.unit}</span>
                      </div>
                      <div
                        className={`notification ${body?.data.quota?.videos.exceeded ? 'warning' : ''}`}>
                        <h2>Limit filmów{body?.data.quota?.videos.exceeded ? ' - wykorzystany!' : ''}</h2>
                        <span>Wykorzystano: {body?.data.quota?.videos.current}/{body?.data.quota?.videos.max} {body?.data.quota?.videos.unit}</span>
                      </div>
                    </div>
                    {body?.data && body.data?.gallery.length > 0 && body.data.gallery.some(item => !item.isPublished) && <>
                      <h2>Nieopublikowane</h2>
                      <div className='photosWrap'>
                        {body.data.gallery.map((photo, index) => (
                          !photo.isPublished ? photo.cdnStatus === 'ERROR'
                            ? <div className="videoError" onClick={() => handleUpload(photo.id)}>
                              <i className="icon icon--retry"></i>
                              Spróbuj ponownie
                            </div>
                            : photo.cdnStatus === 'PENDING' || photo.cdnStatus === 'INIT'
                              ? <div className="videoPending">
                                <i className="icon icon--pending"></i>
                                Ładowanie
                              </div>
                              : <div className='photoWrap'>
                                <img
                                  key={index}
                                  src={photo.isVideo ? photo.thumbnailUrl : photo.url}
                                  alt={photo.displayName}
                                  onClick={() => {
                                    setModal('gallery')
                                    setInitialSlide(index)
                                  }
                                  } />
                                {photo.isVideo && <i className='icon s24 icon--movie'></i>}
                              </div>
                            : undefined
                        ))}
                      </div>
                    </>}
                    {body?.data && body.data?.gallery.length > 0 && body.data.gallery.some(item => item.isPublished)
                      ? <>
                        <h2>Opublikowane</h2>
                        <div className='photosWrap'>
                          {body.data.gallery.map((photo, index) => (
                            photo.isPublished
                              ? photo.cdnStatus === 'ERROR'
                                ? <div className="videoError" onClick={() => handleUpload(photo.id)}>
                                  <i className="icon icon--retry"></i>
                                  Spróbuj ponownie
                                </div>
                                : photo.cdnStatus === 'PENDING' || photo.cdnStatus === 'INIT'
                                  ? <div className="videoPending">
                                    <i className="icon icon--pending"></i>
                                    Ładowanie
                                  </div>
                                  : photo.cdnStatus === ''
                                    ? <div className="videoPending">
                                      <i className="icon icon--pending"></i>
                                      Ładowanie...
                                    </div>
                                    : <div className='photoWrap'
                                      onClick={() => {
                                        setModal('gallery')
                                        setInitialSlide(index)
                                      }
                                      } >
                                      <img
                                        key={index}
                                        src={photo.isVideo ? photo.thumbnailUrl : photo.url}
                                        alt={photo.displayName}
                                      />
                                      {photo.isVideo && <i className='icon s24 icon--movie'></i>}
                                    </div>
                              : undefined
                          ))}
                        </div>
                      </>
                      : undefined
                    }
                  </>)}
                {tab === 'book' && (
                  <div className='booksWrap'>
                    {post && post.length > 0
                      ? <>{post.map(post => (<Post
                        key={post.id}
                        name={post.signature}
                        desc={post.text}
                        date={post.readable_created_at}
                        shouldBePublished={post.shouldBePublished}
                        setModal={setModal}
                        bookIsPublish={post.isPublished}
                        setBookId={() => setBookId(post.id)}
                        addons={
                          !post.isPublished && !post.shouldBePublished ? "Prywatny"
                            : !post.isPublished && post.shouldBePublished ? "Do publikacji"
                              : post.isPublished ? "Publiczny"
                                : ''
                        }
                      />
                      ))}
                        {postHasMore && <button className="btn btn--empty" onClick={loadMorePosts}>Załaduj więcej</button>}
                      </>
                      : <p className='empty'>Brak wpisów w księdze</p>}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div >
    </>)
}

export default TabsManage